import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import * as XLSX from "xlsx";

import { Card, CardBody, CardHeader, Spinner, Row, Col } from "reactstrap";

/* ---------------- HELPERS ---------------- */
const SHIFT_TIME_RANGES = {
  1: { start: 6 * 60, end: 14 * 60 },   // 06:00–14:00
  2: { start: 14 * 60, end: 22 * 60 },  // 14:00–22:00
  3: { start: 22 * 60, end: 30 * 60 },  // 22:00–06:00 (next day)
};

const timeToMinutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToShiftTime = (value, shiftStart) => {
  const total = (shiftStart + value) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};



/* ---------------- TOOLTIP ---------------- */

const TimelineTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <div className="bg-white p-2 border rounded">
      <p><b>Start:</b> {d.start_time}</p>
      <p><b>End:</b> {d.end_time}</p>
      <p><b>Duration:</b> {d.duration} min</p>
      <p><b>Reasons:</b></p>
      <ul style={{ paddingLeft: 16 }}>
        {d.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
    </div>
  );
};

/* ---------------- COMPONENT ---------------- */

const MachineDowntimeTimeLine = ({ machineId, date, shift }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);


 useEffect(() => {
  if (!machineId || !date || !shift) return;

  let intervalId;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.hanoch.in/api/mcc/machine-downtime-reasons/?machine_id=${machineId}&date=${date}&shift=${shift}`
      );
      const result = await res?.json();
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 initial call
  fetchData();

  // 🔁 live update every 1 minute
  intervalId = setInterval(fetchData, 60 * 1000);

  // 🧹 cleanup
  return () => {
    clearInterval(intervalId);
  };
}, [machineId, date, shift]);

//   if (loading) {
//     return (
//       <Card>
//         <CardBody className="text-center">
//           <Spinner size="sm" /> Loading downtime...
//         </CardBody>
//       </Card>
//     );
//   }

  if (!data) return null;

  /* ---------------- TRANSFORMS ---------------- */

  // Timeline data

  const shiftStart = SHIFT_TIME_RANGES[shift].start;
const shiftEnd = SHIFT_TIME_RANGES[shift].end;
const shiftDuration = shiftEnd - shiftStart;

const timelineData = data?.data?.downtimes?.map((d) => {
  let startMin = timeToMinutes(d.start);
  let endMin = timeToMinutes(d.end);

  // Night shift handling
  if (shift === 3 && endMin < startMin) {
    endMin += 24 * 60;
  }

  return {
    start: startMin - shiftStart,     // X position (shift-relative)
    duration: endMin - startMin,      // Y value
    start_time: d.start.slice(0, 5),
    end_time: d.end.slice(0, 5),
    reasons: d.reasons?.map(r => r.description) || [],
  };
});




  // Top reasons bar data
  const reasonData = data?.breakdown?.top_reasons?.map(r => ({
    reason: r.reason,
    count: r.count,
  }));
  const top3Reasons = reasonData?.slice(0, 3);

const exportTimelineToExcel = () => {
  if (!timelineData?.length) return;

  const exportData = timelineData?.map((d, index) => ({
    "S.No": index + 1,
    "Start Time": d.start_time,
    "End Time": d.end_time,
    "Duration (min)": d.duration,
    "Reasons": d.reasons.join(", "),
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Downtime Timeline");

  XLSX.writeFile(
    workbook,
    `Downtime_Timeline_${machineId}_${date}_Shift_${shift}.xlsx`
  );
};

  return (
    <>
<Row className="mt-3">
  {top3Reasons?.map((r, index) => {
    // Split "ST.32 FERRITE FEEDING..." into ["ST.32", "FERRITE", "FEEDING", ...]
    const parts = r.reason.split(" ");
    const stationNumber = parts[0]?.replace("ST.", "");
    const reasonText = parts.slice(1).join(" ");

    return (
      <Col md={4} key={index} className="mb-3">
        <Card
          className="border-0"
          style={{
            height: 90,
            // backgroundColor: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
          }}
        >
          <CardBody className="p-3 d-flex flex-column">
            
            {/* 🔹 Top Row: Station (Left) | Count (Right) */}
            <div className="d-flex justify-content-between align-items-start">
              <span 
                className="fw-bold text-uppercase"
                style={{ 
                  fontSize: "0.8rem", 
                  letterSpacing: "1px", 
                  // color: "rgba(255, 255, 255, 0.6)" 
                }}
              >
                STATION {stationNumber}
              </span>

              <span 
                className="fw-bold" 
                style={{ 
                  // color: "#fff",
                  fontSize: "0.8rem"
                }}
              >
                {r.count}
              </span>
            </div>

            {/* 🔹 Bottom Row: Full Reason Text */}
            <div
              className="mt-1"
              style={{
                fontSize: "13px",
                lineHeight: "16px",
                // color: "#fff",
                fontWeight: "500",
                textTransform: "uppercase",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {reasonText}
            </div>

          </CardBody>
        </Card>
      </Col>
    );
  })}
</Row>

    <Row>
      {/* ---------------- TIMELINE ---------------- */}
      <Col md={8}>
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
  <div>
    <h5 className="mb-0">Downtime Timeline</h5>
    <small className="text-muted">
      Total: {data?.summary?.total_downtime.minutes} (
      {data?.summary?.total_downtime.hours})
    </small>
  </div>

  <button
    className="btn btn-sm btn-outline-success"
    onClick={exportTimelineToExcel}
  >
    Export Excel
  </button>
</CardHeader>


          <CardBody >
             <div style={{ position: "relative", zIndex: 10 }}>
            <ResponsiveContainer width="100%" height={220}>
             <BarChart
  data={timelineData}
  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
>

        <XAxis
  dataKey="start"          // 🔥 THIS IS MISSING
  type="number"
  domain={[0, shiftDuration]}
  ticks={Array.from(
    { length: shiftDuration / 60 + 1 },
    (_, i) => i * 60
  )}
  tickFormatter={(v) => minutesToShiftTime(v, shiftStart)}
/>


<YAxis
  type="number"
  label={{
    value: "Duration(min)",
    angle: -90,
    position: "insideLeft",
  }}
/>



           <Tooltip content={<TimelineTooltip />} />

<Bar
  dataKey="duration"
  fill="#4fd3ff97"
  barSize={10}
/>

              </BarChart>
            </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* ---------------- TOP REASONS ---------------- */}
      <Col md={4}>
        <Card>
          <CardHeader>
            <h5>Top Downtime Reasons</h5>
          </CardHeader>

          <CardBody>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={reasonData}
              
              barCategoryGap="40%" >
                <XAxis dataKey="reason" hide />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#52b788"
                barSize={20}   />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      </Col>
    </Row>
    </>
  );
};

export default MachineDowntimeTimeLine;
